// src/components/ProjectCreate.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import * as ReactDOM from 'react-dom';
import { createProject, startHearing, sendMessage, sendMessageStreaming, completeHearing } from '../services/api';

const ProjectCreate = () => {
  // 既存のステート
  const [formData, setFormData] = useState({
    content: '',
    assignee: '',
    clientName: ''
  });
  const [isHearingStarted, setIsHearingStarted] = useState(false);
  const [projectId, setProjectId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [currentStreamMessage, setCurrentStreamMessage] = useState('');
  const messagesEndRef = useRef(null);
  const eventSourceRef = useRef(null);
  const navigate = useNavigate();

  // DOM操作を使用して即時表示するカスタムフック
  const useStreamingDisplay = () => {
    const containerRef = useRef(null);
    const textContentRef = useRef('');
    
    const updateText = useCallback((newChunk) => {
      if (containerRef.current) {
        textContentRef.current += newChunk;
        // 直接DOMを操作して表示を更新
        containerRef.current.innerHTML = textContentRef.current;
      }
      return textContentRef.current;
    }, []);
    
    const resetText = useCallback(() => {
      textContentRef.current = '';
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    }, []);
    
    return {
      containerRef,
      updateText,
      resetText,
      currentText: textContentRef.current
    };
  };

  // フックを使用
  const { containerRef, updateText, resetText } = useStreamingDisplay();
  
  // フォーム入力の処理
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // メッセージ表示領域を自動スクロール
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, currentStreamMessage]);

  // コンポーネントのアンマウント時にストリーミングをクリーンアップ
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);

  // ヒアリング開始処理
  const handleStartHearing = async () => {
    // 入力検証
    if (!formData.content || !formData.assignee || !formData.clientName) {
      setError('全ての項目を入力してください');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // 1. プロジェクトをデータベースに作成
      const projectResponse = await createProject(formData);
      const newProjectId = projectResponse.id;
      setProjectId(newProjectId);
      
      // 2. AIによるヒアリング開始
      const hearingResponse = await startHearing(newProjectId, formData.content);
      
      // 3. 初期メッセージを表示
      setMessages([
        { role: 'system', content: 'ヒアリングを開始します。' },
        { role: 'assistant', content: hearingResponse.message }
      ]);
      
      setIsHearingStarted(true);
    } catch (err) {
      setError(err.message || 'ヒアリングの開始に失敗しました');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // メッセージ送信処理（ストリーミング）
  const handleSendMessage = async (e) => {
    e.preventDefault();
    const userMessage = e.target.message.value;
    if (!userMessage.trim() || isLoading || isStreaming) return;
  
    // ユーザーメッセージを表示
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    e.target.message.value = '';
    
    setIsStreaming(true);
    setCurrentStreamMessage('');
    setError(null); // エラーメッセージをクリア
    
    try {
      console.log('ストリーミングプロセス開始...');
      
      // ストリーミングを開始
      eventSourceRef.current = await sendMessageStreaming(
        projectId,
        userMessage,
        messages.filter(msg => msg.role !== 'system'),
        // 部分レスポンス受信時
        (chunk) => {
          console.log('チャンク受信:', chunk);
          // DOM直接操作で更新
          updateText(chunk);
          // バックアップとしてReactのステートも更新
          setCurrentStreamMessage(prev => prev + chunk);
        },
        // 完了時
        (fullResponse) => {
          console.log('ストリーミング完了');
          setIsStreaming(false);
          setMessages(prev => [...prev, { role: 'assistant', content: fullResponse }]);
          setCurrentStreamMessage('');
          resetText(); // リセット
        },
        // エラー時は同じまま
        (errorMsg) => {
          console.error('ストリーミングエラー詳細:', errorMsg);
          setIsStreaming(false);
          setMessages(prev => [...prev, { 
            role: 'system', 
            content: 'エラーが発生しました: ' + errorMsg
          }]);
          setCurrentStreamMessage('');
          setError('ストリーミングエラー: ' + errorMsg);
          resetText(); // リセット
        }
      );
    } catch (err) {
      console.error('メッセージ送信初期化エラー:', err);
      setIsStreaming(false);
      setMessages(prev => [...prev, { 
        role: 'system', 
        content: 'メッセージ送信エラー: ' + err.message 
      }]);
      setError('送信エラー: ' + err.message);
    }
  };

  // ヒアリング終了処理
  const handleFinishHearing = async () => {
    if (!projectId || messages.length < 3) {
      setError('ヒアリングが不十分です。もう少し会話を続けてください。');
      return;
    }

    // ストリーミング中なら中止
    if (isStreaming && eventSourceRef.current) {
      eventSourceRef.current.close();
      setIsStreaming(false);
      setCurrentStreamMessage('');
    }

    setIsLoading(true);
    try {
      // ヒアリング内容を要約
      await completeHearing(projectId);
      
      // 詳細ページに遷移
      navigate(`/${projectId}`);
    } catch (err) {
      setError(err.message || 'ヒアリングの終了処理に失敗しました');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // マークダウンメッセージのレンダリング
  const renderMessage = (content) => {
    return (
      <ReactMarkdown>
        {content}
      </ReactMarkdown>
    );
  };

  return (
    <div className="container mx-auto p-4">
      <div className="mb-6">
        <Link to="/" className="text-blue-500 hover:text-blue-700">
          ← 一覧に戻る
        </Link>
      </div>
      
      <h1 className="text-2xl font-bold mb-6">新規ヒアリング作成</h1>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {!isHearingStarted ? (
        // ヒアリング開始前のフォーム
        <div className="bg-white p-6 rounded shadow-md">
          <div className="mb-4">
            <label className="block text-gray-700 mb-2" htmlFor="content">相談内容</label>
            <textarea
              id="content"
              name="content"
              value={formData.content}
              onChange={handleChange}
              className="w-full px-3 py-2 border rounded"
              rows="4"
              placeholder="例: ECサイトを作りたい。予算は100万円くらいで考えています。"
              required
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 mb-2" htmlFor="assignee">担当者</label>
            <input
              type="text"
              id="assignee"
              name="assignee"
              value={formData.assignee}
              onChange={handleChange}
              className="w-full px-3 py-2 border rounded"
              placeholder="例: 山田太郎"
              required
            />
          </div>
          
          <div className="mb-6">
            <label className="block text-gray-700 mb-2" htmlFor="clientName">クライアント名</label>
            <input
              type="text"
              id="clientName"
              name="clientName"
              value={formData.clientName}
              onChange={handleChange}
              className="w-full px-3 py-2 border rounded"
              placeholder="例: 株式会社サンプル"
              required
            />
          </div>
          
          <button
            onClick={handleStartHearing}
            disabled={isLoading}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded w-full disabled:bg-gray-400"
          >
            {isLoading ? (
              <>
                <span className="inline-block animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></span>
                処理中...
              </>
            ) : 'ヒアリングを始める'}
          </button>
        </div>
      ) : (
        // ヒアリング中のチャットUI
        <div className="flex flex-col h-[calc(100vh-160px)]">
          <div className="flex-grow bg-white p-4 rounded shadow-md overflow-y-auto mb-4">
            {messages.map((msg, idx) => (
              <div 
                key={idx} 
                className={`mb-4 ${
                  msg.role === 'user' 
                    ? 'bg-blue-100 ml-12' 
                    : msg.role === 'assistant' 
                      ? 'bg-gray-100 mr-12' 
                      : 'bg-yellow-100 text-center'
                } p-3 rounded`}
              >
                {msg.role === 'system' ? (
                  msg.content
                ) : (
                  <div className="prose max-w-none">
                    {renderMessage(msg.content)}
                  </div>
                )}
              </div>
            ))}
            
            {/* ストリーミング中のメッセージ */}
            {isStreaming && (
              <div className="mb-4 bg-gray-100 p-3 rounded">
                <div className="text-base text-gray-900 leading-relaxed">
                  <div 
                    ref={containerRef} 
                    className="markdown-content space-y-4 font-normal break-words"
                  ></div>
                  <span className="inline-block animate-pulse">▋</span>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
          
          <div className="bg-white p-4 rounded shadow-md">
            <form onSubmit={handleSendMessage} className="flex gap-2">
              <textarea
                type="text"
                name="message"
                className="flex-grow px-3 py-2 border rounded"
                placeholder={isStreaming ? "AIが回答中です..." : "メッセージを入力..."}
                disabled={isLoading || isStreaming}
              />
              <button
                type="submit"
                disabled={isLoading || isStreaming}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded disabled:bg-gray-400"
              >
                送信
              </button>
            </form>
            
            <button
              onClick={handleFinishHearing}
              disabled={isLoading || (isStreaming && messages.length < 3)}
              className="mt-4 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded w-full disabled:bg-gray-400"
            >
              {isLoading ? (
                <>
                  <span className="inline-block animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></span>
                  処理中...
                </>
              ) : 'ヒアリング終了'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectCreate;